if (!customElements.get('ey-site-navigation')) {
  customElements.define('ey-site-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('.ey-nav-dialog');
      this.toggle = this.querySelector('.ey-nav-toggle');
      this.closeButton = this.querySelector('.ey-nav-close');
      if (!this.dialog || !this.toggle || !this.closeButton) return;

      this.controller = new AbortController();
      this.cartRevision = 0;
      this.cartEventRevision = 0;
      const options = { signal: this.controller.signal };
      this.toggle.addEventListener('click', () => this.openMenu(), options);
      this.closeButton.addEventListener('click', () => this.closeMenu(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeMenu();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.restorePage();
      }, options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.pointerStartedOutside = this.isOutsideDialog(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.pointerStartedOutside && this.isOutsideDialog(event)) this.closeMenu();
        this.pointerStartedOutside = false;
        if (event.target instanceof Element && event.target.closest('a[href]')) this.closeMenu();
      }, options);
      window.addEventListener('pageshow', () => this.refreshCart(), options);
      window.addEventListener('focus', () => this.refreshCart(), options);
      document.addEventListener('shopify:cart:lines-update', (event) => this.onCartChange(event), options);
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.cartRevision += 1;
      if (this.dialog?.open) this.dialog.close();
      this.restorePage(false);
    }

    openMenu() {
      if (this.dialog.open) return;
      this.previousFocus = document.activeElement;
      this.dialog.showModal();
      this.scrollStyles = [document.documentElement, document.body].map((element) => ({
        element,
        value: element.style.getPropertyValue('overflow'),
        priority: element.style.getPropertyPriority('overflow'),
      }));
      this.scrollStyles.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
      this.toggle.setAttribute('aria-expanded', 'true');
      this.closeButton.focus({ preventScroll: true });
      this.refreshCart();
    }

    closeMenu() {
      if (this.dialog.open) this.dialog.close();
      this.restorePage();
    }

    restorePage(restoreFocus = true) {
      if (!this.scrollStyles) return;
      this.scrollStyles.forEach(({ element, value, priority }) => {
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      this.scrollStyles = null;
      this.toggle?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) {
        const target = this.previousFocus instanceof HTMLElement && this.previousFocus.isConnected
          ? this.previousFocus
          : this.toggle;
        target?.focus({ preventScroll: true });
      }
      this.previousFocus = null;
    }

    isOutsideDialog(event) {
      const bounds = this.dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX >= bounds.right
        || event.clientY < bounds.top || event.clientY >= bounds.bottom;
    }

    setCartCount(value) {
      if (!Number.isInteger(value) || value < 0) return false;
      this.querySelectorAll('.ey-nav-count').forEach((element) => {
        element.textContent = String(value);
      });
      this.querySelectorAll('.ey-nav-bag[data-cart-label]').forEach((link) => {
        link.setAttribute('aria-label', `${link.dataset.cartLabel} (${value})`);
      });
      return true;
    }

    async onCartChange(event) {
      const eventRevision = ++this.cartEventRevision;
      this.cartRevision += 1;
      const signal = this.controller.signal;
      try {
        // Shopify standard events carry the payload fields directly on the event.
        const result = event.promise ? await event.promise : null;
        if (signal.aborted || eventRevision !== this.cartEventRevision) return;
        this.cartRevision += 1;
        if (this.setCartCount(result?.cart?.totalQuantity)) return;
        if (this.setCartCount(result?.detail?.itemCount)) return;
        if (this.setCartCount(event.detail?.itemCount)) return;
      } catch {
        if (signal.aborted || eventRevision !== this.cartEventRevision) return;
      }
      this.refreshCart();
    }

    async refreshCart() {
      if (!this.controller || !this.dataset.cartUrl) return;
      const revision = ++this.cartRevision;
      const signal = this.controller.signal;
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal,
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (!signal.aborted && revision === this.cartRevision) this.setCartCount(cart.item_count);
      } catch {
        // Keep the last confirmed Liquid/event count if a background refresh fails.
      }
    }
  });
}
